import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Play, Image, ChevronLeft, ChevronRight, X, Film, Camera, CheckCircle, Clock, Rocket, ZoomIn } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stage Icons
const stageIcons = {
  'Beginning': Rocket,
  'In Progress': Clock,
  'Final Product': CheckCircle,
};

// Fullscreen Lightbox Component (same as Gallery)
const MediaLightbox = ({ media, initialIndex, onClose, projectTitle, stageName }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  }, [media.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  }, [media.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, goToPrevious, goToNext]);

  const currentMedia = media[currentIndex];
  const isVideo = currentMedia?.type === 'video';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <button onClick={onClose} className="absolute top-4 right-4 z-10 bg-black/60 hover:bg-[#D4AF37] text-white p-3 rounded-full transition-all">
        <X size={24} />
      </button>

      <div className="absolute top-4 left-4 z-10 bg-black/60 text-white px-4 py-2 rounded-full">
        <span className="text-[#D4AF37] font-bold">{currentIndex + 1}</span>
        <span className="text-white/60"> / {media.length}</span>
      </div>

      {/* Title above image */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-black/60 text-white px-6 py-3 rounded-lg text-center">
        <h3 className="text-xl font-bold text-white/80">{projectTitle}</h3>
        <p className="text-sm text-[#D4AF37]">{stageName}</p>
      </div>

      <div className="relative w-full h-full flex items-center justify-center p-4 md:p-16 pt-24" onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          {isVideo ? (
            <motion.video
              key={currentIndex}
              src={currentMedia.url}
              controls
              autoPlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="max-w-full max-h-full rounded-lg"
            />
          ) : (
            <motion.img
              key={currentIndex}
              src={currentMedia.url}
              alt=""
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="max-w-full max-h-full object-contain"
            />
          )}
        </AnimatePresence>
      </div>

      {media.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); goToPrevious(); }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#D4AF37] text-white p-4 rounded-full transition-all">
            <ChevronLeft size={32} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); goToNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#D4AF37] text-white p-4 rounded-full transition-all">
            <ChevronRight size={32} />
          </button>
        </>
      )}

      {media.length > 1 && media.length <= 20 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/60 p-2 rounded-lg max-w-[90vw] overflow-x-auto">
          {media.map((item, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
              className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all relative ${
                index === currentIndex ? 'border-[#D4AF37] scale-110' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              {item.type === 'video' ? (
                <div className="w-full h-full bg-black/80 flex items-center justify-center">
                  <Play size={20} className="text-[#D4AF37]" />
                </div>
              ) : (
                <img src={item.url} alt="" className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}

      <div className="absolute bottom-4 right-4 text-white/40 text-xs hidden md:block">
        ESC to close • Arrow keys to navigate
      </div>
    </motion.div>
  );
};

// Stage Carousel Component (like Gallery carousel)
const StageCarousel = ({ stage, stageName, onImageClick, projectTitle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  const StageIcon = stageIcons[stageName] || Camera;
  
  // Collect all media (images + video)
  const allMedia = [
    ...(stage.images || []).filter(url => url && url.trim() !== '').map(url => ({ type: 'image', url })),
    ...(stage.video_url ? [{ type: 'video', url: stage.video_url }] : [])
  ];

  // Auto-advance every 4 seconds
  useEffect(() => {
    if (allMedia.length <= 1 || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % allMedia.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [allMedia.length, isPaused]);

  if (allMedia.length === 0) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
            <StageIcon size={20} className="text-[#D4AF37]" />
          </div>
          <div>
            <h4 className="font-bold text-white">{stageName}</h4>
            {stage.description && <p className="text-xs text-white/50">{stage.description}</p>}
          </div>
        </div>
        <div className="h-48 bg-black/20 rounded flex items-center justify-center">
          <p className="text-white/30 text-sm">No media added</p>
        </div>
      </div>
    );
  }

  const goToPrevious = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
  };

  const goToNext = (e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
  };

  const handleClick = () => {
    if (onImageClick) {
      onImageClick(allMedia, currentIndex, stageName);
    }
  };

  const currentMedia = allMedia[currentIndex];

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:border-[#D4AF37]/30 transition-all">
      {/* Stage Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
          <StageIcon size={20} className="text-[#D4AF37]" />
        </div>
        <div className="flex-1">
          <h4 className="font-bold text-white">{stageName}</h4>
          {stage.description && <p className="text-xs text-white/50">{stage.description}</p>}
        </div>
        <div className="text-xs text-white/40">
          {allMedia.length} {allMedia.length === 1 ? 'item' : 'items'}
        </div>
      </div>

      {/* Carousel */}
      <div 
        className="relative h-56 bg-black/20 rounded overflow-hidden group cursor-pointer flex items-center justify-center"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onClick={handleClick}
      >
        {/* Current Media */}
        <AnimatePresence mode="wait">
          {currentMedia.type === 'video' ? (
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full h-full flex items-center justify-center bg-black/60"
            >
              <Play size={48} className="text-[#D4AF37]" />
              <span className="absolute bottom-2 left-2 text-xs text-white/60 bg-black/60 px-2 py-1 rounded">Video</span>
            </motion.div>
          ) : (
            <motion.img
              key={currentIndex}
              src={currentMedia.url}
              alt=""
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-full max-h-full object-contain"
            />
          )}
        </AnimatePresence>

        {/* Zoom indicator on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center pointer-events-none">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/60 p-3 rounded-full">
            <ZoomIn size={24} className="text-[#D4AF37]" />
          </div>
        </div>

        {allMedia.length > 1 && (
          <>
            {/* Left Arrow */}
            <button 
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#D4AF37] text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-10"
            >
              <ChevronLeft size={20} />
            </button>
            
            {/* Right Arrow */}
            <button 
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#D4AF37] text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-10"
            >
              <ChevronRight size={20} />
            </button>
            
            {/* Dots Indicator */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
              {allMedia.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-[#D4AF37] scale-125' 
                      : 'bg-white/40 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
            
            {/* Counter & Auto-rotate indicator */}
            <div className="absolute top-2 right-2 flex items-center gap-2 z-10">
              <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-white/50' : 'bg-[#D4AF37] animate-pulse'}`} />
              <span className="bg-black/60 text-white text-xs px-2 py-1 rounded">
                {currentIndex + 1} / {allMedia.length}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Thumbnail Strip */}
      {allMedia.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {allMedia.map((item, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`flex-shrink-0 w-14 h-14 rounded overflow-hidden border-2 transition-all ${
                index === currentIndex 
                  ? 'border-[#D4AF37] scale-105' 
                  : 'border-white/10 opacity-60 hover:opacity-100'
              }`}
            >
              {item.type === 'video' ? (
                <div className="w-full h-full bg-black/60 flex items-center justify-center">
                  <Play size={16} className="text-[#D4AF37]" />
                </div>
              ) : (
                <img src={item.url} alt="" className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Project Card
const ProjectCard = ({ project, index }) => {
  const [expanded, setExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxStage, setLightboxStage] = useState('');

  const defaultStages = [
    { name: 'Beginning', description: '', images: [], video_url: '' },
    { name: 'In Progress', description: '', images: [], video_url: '' },
    { name: 'Final Product', description: '', images: [], video_url: '' },
  ];

  const stages = project.stages?.length > 0 ? project.stages : defaultStages;

  const handleViewMedia = (media, idx, stageName) => {
    setLightboxMedia(media);
    setLightboxIndex(idx);
    setLightboxStage(stageName);
    setLightboxOpen(true);
  };

  // Count total media
  const totalMedia = stages.reduce((acc, stage) => {
    const images = (stage.images || []).filter(img => img && img.trim() !== '');
    return acc + images.length + (stage.video_url ? 1 : 0);
  }, 0);

  return (
    <>
      <AnimatePresence>
        {lightboxOpen && (
          <MediaLightbox
            media={lightboxMedia}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
            projectTitle={project.title}
            stageName={lightboxStage}
          />
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className="border border-white/10 hover:border-[#D4AF37]/30 transition-all bg-white/5 rounded-lg overflow-hidden"
        data-testid={`project-card-${index}`}
      >
        {/* Thumbnail */}
        <div className="relative h-64 bg-black/40 flex items-center justify-center">
          {project.thumbnail ? (
            <img src={project.thumbnail} alt={project.title} className="max-w-full max-h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Film size={48} className="text-white/20" />
            </div>
          )}
          {project.featured && (
            <div className="absolute top-3 right-3 bg-[#D4AF37] text-black px-3 py-1 text-xs font-bold rounded">
              FEATURED
            </div>
          )}
          <div className="absolute bottom-3 left-3 bg-black/60 px-3 py-1 rounded text-sm">
            <span className="text-[#D4AF37]">{totalMedia}</span> <span className="text-white/60">media files</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="text-2xl font-bold mb-2 text-white">{project.title}</h3>
          {project.client && (
            <p className="text-sm text-[#D4AF37] mb-3">Client: {project.client}</p>
          )}
          <p className="text-white/70 mb-4">{project.description}</p>

          <Button
            onClick={() => setExpanded(!expanded)}
            variant="outline"
            className="w-full border-white/20 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
          >
            {expanded ? 'Hide Stages' : 'View Project Stages'}
          </Button>

          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 gap-4 mt-4 pt-4 border-t border-white/10">
                  {stages.map((stage, idx) => (
                    <StageCarousel
                      key={idx}
                      stage={stage}
                      stageName={stage.name || `Stage ${idx + 1}`}
                      onImageClick={handleViewMedia}
                      projectTitle={project.title}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-32 px-6 flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-16 px-6 md:px-12 lg:px-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-black mb-4" data-testid="projects-title">OUR PROJECTS</h1>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Follow our manufacturing journey from concept to completion. Each project showcases our commitment to precision and quality.
          </p>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-20 border border-white/10 rounded-lg">
            <Film size={48} className="mx-auto mb-4 text-white/20" />
            <p className="text-white/50">No projects yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </div>
        )}

        {/* Coming Soon Notice */}
        <div className="mt-12 p-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-lg text-center">
          <p className="text-[#D4AF37] font-bold mb-1">More Projects Coming Soon</p>
          <p className="text-white/60 text-sm">We're continuously adding new project showcases. Check back regularly for updates!</p>
        </div>
      </motion.div>
    </div>
  );
};

export default Projects;
